/**
 * useScenariosUndo Hook
 *
 * Manages the undo-able delete workflow for scenarios:
 * - Immediate Firestore delete on request
 * - Cached scenario data for undo re-creation
 * - Auto-dismiss timers for toast UI
 * - Timer cleanup on unmount
 *
 * Delegates actual Firestore operations (delete, restore) to caller-provided
 * callbacks so this hook remains free of state management concerns.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import type { Scenario } from "../types/scenario";
import type { UndoToastItem } from "../components/UndoToast";

/** Duration in ms before a deferred delete is executed against Firestore */
const DEFAULT_UNDO_DURATION = 5000;

interface UseScenariosUndoArgs {
  undoDuration?: number;
  /** Execute the Firestore delete and return success/failure */
  onDelete: (scenarioId: string) => Promise<boolean>;
  /** Re-save a previously deleted scenario (called on undo) */
  onRestore: (scenario: Scenario) => void;
}

interface UseScenariosUndoReturn {
  pendingDeletes: UndoToastItem[];
  /** Initiate a delete with undo window. Caller should handle optimistic UI. */
  requestDelete: (scenario: Scenario) => Promise<boolean>;
  undoDelete: (scenarioId: string) => void;
  confirmDelete: (scenarioId: string) => void;
}

export function useScenariosUndo({
  undoDuration = DEFAULT_UNDO_DURATION,
  onDelete,
  onRestore,
}: UseScenariosUndoArgs): UseScenariosUndoReturn {
  const [pendingDeletes, setPendingDeletes] = useState<UndoToastItem[]>([]);
  const deleteTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const deletedScenariosCache = useRef<Map<string, Scenario>>(new Map());

  const requestDelete = useCallback(
    async (scenario: Scenario): Promise<boolean> => {
      const success = await onDelete(scenario.id);
      if (!success) return false;

      // Cache full data for potential undo
      deletedScenariosCache.current.set(scenario.id, scenario);

      // Track for toast UI
      const toastItem: UndoToastItem = {
        id: scenario.id,
        scenarioName: scenario.name,
        startedAt: Date.now(),
      };
      setPendingDeletes((prev) => [...prev, toastItem]);

      // Auto-dismiss after undo window expires
      const timer = setTimeout(() => {
        deleteTimers.current.delete(scenario.id);
        deletedScenariosCache.current.delete(scenario.id);
        setPendingDeletes((prev) => prev.filter((d) => d.id !== scenario.id));
      }, undoDuration);
      deleteTimers.current.set(scenario.id, timer);

      return true;
    },
    [onDelete, undoDuration]
  );

  const undoDelete = useCallback(
    (scenarioId: string) => {
      const timer = deleteTimers.current.get(scenarioId);
      if (timer) {
        clearTimeout(timer);
        deleteTimers.current.delete(scenarioId);
      }
      setPendingDeletes((prev) => prev.filter((d) => d.id !== scenarioId));

      const cached = deletedScenariosCache.current.get(scenarioId);
      if (!cached) return;
      deletedScenariosCache.current.delete(scenarioId);

      onRestore(cached);
    },
    [onRestore]
  );

  const confirmDelete = useCallback((scenarioId: string) => {
    const timer = deleteTimers.current.get(scenarioId);
    if (timer) {
      clearTimeout(timer);
      deleteTimers.current.delete(scenarioId);
    }
    deletedScenariosCache.current.delete(scenarioId);
    setPendingDeletes((prev) => prev.filter((d) => d.id !== scenarioId));
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = deleteTimers.current;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
    };
  }, []);

  return { pendingDeletes, requestDelete, undoDelete, confirmDelete };
}
